import { create } from 'zustand';
import axios from 'axios';

interface Branding {
  headerText: string;
  sloganText: string;
  copyrightText: string;
  logoUrl: string | null;
}

interface BrandingStore {
  branding: Branding;
  isLoading: boolean;
  error: string | null;
  
  fetchBranding: () => Promise<void>;
  updateBranding: (data: Partial<Branding>) => void;
}

const DEFAULT_BRANDING: Branding = {
  headerText: 'MedicalAI',
  sloganText: 'Healthcare Management Platform',
  copyrightText: '© 2026 Victor Levitan, MD, PLLC, Internal Medicine in Highland Park, Illinois - All Rights Reserved.',
  logoUrl: null,
};

// Create a separate axios instance for branding (no auth needed for public endpoint)
const brandingAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://levitan.practicepilot.ai/api',
});

export const useBrandingStore = create<BrandingStore>()((set) => ({
  branding: DEFAULT_BRANDING,
  isLoading: false,
  error: null,

  fetchBranding: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await brandingAxios.get('/branding');
      const fetchedBranding = response.data?.branding || {};
      
      set({
        branding: {
          headerText: fetchedBranding.headerText || DEFAULT_BRANDING.headerText,
          sloganText: fetchedBranding.sloganText || DEFAULT_BRANDING.sloganText,
          copyrightText: fetchedBranding.copyrightText || DEFAULT_BRANDING.copyrightText,
          logoUrl: fetchedBranding.logoUrl || null,
        },
        isLoading: false,
      });

      // Update document title
      document.title = `${fetchedBranding.headerText || DEFAULT_BRANDING.headerText} - ${fetchedBranding.sloganText || DEFAULT_BRANDING.sloganText}`;

      // Update favicon if custom logo is set
      if (fetchedBranding.logoUrl) {
        const link = document.getElementById('favicon') as HTMLLinkElement;
        if (link) {
          link.href = fetchedBranding.logoUrl;
        }
      }
    } catch (error: any) {
      console.error('[BRANDING] Failed to fetch branding:', error);
      set({
        branding: DEFAULT_BRANDING,
        isLoading: false,
        error: error.message || 'Failed to load branding',
      });
      // Set default title even on error
      document.title = `${DEFAULT_BRANDING.headerText} - ${DEFAULT_BRANDING.sloganText}`;
    }
  },

  updateBranding: (data: Partial<Branding>) => {
    set((state) => {
      const newBranding = { ...state.branding, ...data };
      
      // Update document title when branding changes
      document.title = `${newBranding.headerText} - ${newBranding.sloganText}`;
      
      // Update favicon if logo changes
      if (data.logoUrl !== undefined) {
        const link = document.getElementById('favicon') as HTMLLinkElement;
        if (link && data.logoUrl) {
          link.href = data.logoUrl;
        }
      }
      
      return { branding: newBranding };
    });
  },
}));

